import request from '@/utils/request';
import type { R, RL, RP, ApiResult, TenantStudent, KV } from '@/api';
import { RParam } from '@/api';
import fileUtil from '@/utils/file-util';
import LzdUtil, {
  getAppId,
  getDataScopeId,
  getRoleCode,
  JSONUtil
} from '@/utils/lzd-util';
import LzdAjax from '@/utils/lzd-ajax';
import type { ProjectInstTask } from '@/api/bsl/project/instance/task/model';
import { GLOBAL_CONFIG_INST_ID } from '@/api/bsl/project/instance/model';
import type { DocumentInst } from '@/api/bsl/document/types';

const BASE_URL = '/bsl/tenant/students';

/**
 * 分页查询用户
 */
export async function pageTenantStudent(params?: RP) {
  params = params ?? { where: [] };
  params.where = params.where ?? [];
  return LzdAjax.post<RL<TenantStudent>>(
    `${BASE_URL}/query/page`,
    params,
    false,
    true
  ).then((v) => v.data ?? { count: 0, list: [] });
}

/**
 * 分页查询用户
 */
export async function teacherQueryExpPage(params?: RP) {
  params = params ?? { where: [] };
  params.where = params.where ?? [];
  return LzdAjax.post<RL<TenantStudent>>(
    `${BASE_URL}/teacher/query/exp/page`,
    params,
    false,
    true
  ).then((v) => v.data ?? { count: 0, list: [] });
}

export interface StudentExpertStats {
  total: number;
  selected: number;
  assigned: number;
}

/** 当前学年及管理范围内的学生评阅分配统计 */
export async function teacherQueryExpStats() {
  return LzdAjax.post<StudentExpertStats>(
    `${BASE_URL}/teacher/query/exp/stats`,
    { where: [] },
    false,
    true
  ).then((v) => v.data);
}

/**
 * 保存信息
 */
export async function saveTenantStudent(data: TenantStudent) {
  const formData = new FormData();
  data.dataScopeId = getDataScopeId();
  formData.append('tenantStudentJson', JSON.stringify(data));
  formData.append('password', data.password as string);
  const res = await request.post<ApiResult<unknown>>(
    `${BASE_URL}/insert`,
    formData
  );
  return res.data.code === 0 ? res.data : Promise.reject(res.data);
}

/**
 * 修改信息
 */
export async function updateTenantStudent(data: TenantStudent) {
  const res = await request.post<ApiResult<unknown>>(`${BASE_URL}/update`, {
    //字段修改
    update: RParam.buildEdit(data, (k: string) => k !== 'seq'),
    //修改条件
    where: [RParam.eq('seq', data.seq), RParam.eq('password', data.password)],
    tenantId: data.tenantId,
    dataScopeId: data.dataScopeId
  });
  return res.data.code === 0 ? res.data : Promise.reject(res.data);
}

/**
 * 批量删除用户信息
 */
export async function removeTenantStudent(
  data: (number | undefined)[],
  tenantId?: string,
  dataScopeId?: number
) {
  const param: RP = {
    where: RParam.buildWhere({ seq: data }),
    tenantId: tenantId,
    dataScopeId: dataScopeId
  };
  const res = await request.post<ApiResult<unknown>>(
    `${BASE_URL}/batch/delete`,
    param
  );
  return res.data.code === 0 ? res.data : Promise.reject(res.data);
}

/**
 * 导入学生数据
 */
export async function importTenantStudent(param: RP, file: File) {
  const formData = new FormData();

  param.appId = getAppId();
  param.roleCode = getRoleCode();
  param.dataScopeId = getDataScopeId();

  formData.append('param', JSONUtil.toStr(param));
  formData.append('file', file);
  const res = await request.post<ApiResult<unknown>>(
    `${BASE_URL}/import`,
    formData
  );
  return res.data.code === 0 ? res.data : Promise.reject(res.data);
}

/**
 * 导出学生信息
 */
export async function exportTenantStduent(param: RP) {
  fileUtil.downloadPostFile(`${BASE_URL}/export`, {
    param: JSONUtil.toStr(param)
  });
}

/**
 * 重置学生密码
 * @param userid
 * @param password 密码
 * @param tenantId 机构编码
 */
export async function resetStudentPassword(
  userid: string,
  password: string,
  tenantId?: string,
  dataScopeId?: number
) {
  const res = await request.post<ApiResult<unknown>>(
    `${BASE_URL}/reset/password`,
    {
      update: RParam.buildEdit({ password }),
      where: [RParam.eq('userid', userid)],
      tenantId: tenantId,
      dataScopeId: dataScopeId
    }
  );
  return res.data.code === 0 ? res.data : Promise.reject(res.data);
}

/**
 * 批量重置学生用户密码
 */
export async function batchResetPassword(params: RP) {
  const res = await request.post<ApiResult<unknown>>(
    `${BASE_URL}/batch/reset/password`,
    params
  );
  return res.data;
}

/**
 * 获取单个学生数据
 */
export async function getTenantStudent(params: RP = { where: [] }) {
  params.where = params.where ?? [];
  return LzdAjax.post<TenantStudent>(
    `${BASE_URL}/query/single`,
    params,
    false,
    true
  ).then((v) => v.data);
}

/**
 * 查询学生信息 列表
 */
export async function queryTenantStudents(params: RP = { where: [] }) {
  params.where = params.where ?? [];
  return LzdAjax.post<TenantStudent[]>(
    BASE_URL + '/manager/query/list',
    params,
    false,
    true
  ).then((v) => v.data ?? []);
}

/**
 * 查询学生信息 列表
 */
export async function queryTenantStudentsBy(data: Partial<TenantStudent> = {}) {
  return queryTenantStudents({ where: RParam.buildWhere(data) });
}

/** 根据教师ID查询指导的学生 */
async function queryTenantStudentsByTeacherIds(teacherIds: string[]) {
  if (!teacherIds || teacherIds.length === 0) {
    return LzdUtil.tipsParameterError();
  }
  return queryTenantStudents({
    where: RParam.buildWhere({ advisorId: teacherIds })
  });
}

/** 教师查询指导的学生 */
async function queryTenantStudentsByTeacher(teacherId: string) {
  if (!teacherId) {
    return LzdUtil.tipsParameterError();
  }
  return LzdAjax.post<TenantStudent[]>(
    BASE_URL + '/teacher/query/list',
    {
      where: RParam.buildWhere({ advisorId: teacherId })
    },
    false,
    true
  ).then((v) => v.data ?? []);
}

/**
 * 根据账号和专业查询
 * @param userid 账号
 * @param major 专业
 */
async function querySingleByUserIdAndMajor(userid: string, major: string) {
  if (!userid || !major) {
    return LzdUtil.tipsParameterError();
  }
  return LzdAjax.post<TenantStudent>(
    `${BASE_URL}/query/single`,
    { where: RParam.buildWhere({ userid, major }) },
    false,
    true
  ).then((v) => v.data);
}

/**
 * 根据项目ID和专业查询学生信息
 * @param projectId 账号
 * @param major 专业
 */
async function querySingleByTask(task: ProjectInstTask) {
  if (!task) {
    return LzdUtil.tipsError('task is null');
  }
  if (!task.projectId || task.projectId === GLOBAL_CONFIG_INST_ID) {
    return LzdUtil.tipsError('projectId is error');
  }
  if (!task.major) {
    return LzdUtil.tipsError('major is error');
  }
  const { projectId, major } = task;
  return LzdAjax.post<TenantStudent>(
    `/bsl/project/inst/query/single/student-info`,
    { where: RParam.buildWhere({ projectId, major }) },
    false,
    true
  ).then((v) => v.data);
}

/**
 * 管理员查询学生列表
 */
async function managerQueryList(param: RP) {
  return LzdAjax.post<TenantStudent[]>(
    BASE_URL + '/manager/query/list',
    param,
    false,
    true
  ).then((v) => v.data ?? []);
}

/**
 * 教师查询学生列表
 */
async function teacherQueryList(param: RP) {
  return LzdAjax.post<TenantStudent[]>(
    BASE_URL + '/teacher/query/list',
    param,
    false,
    true
  ).then((v) => v.data ?? []);
}

/** 个人中心更新学生通过userid */
async function studentUpdateByUserid(data: TenantStudent) {
  if (!data.userid) {
    return LzdUtil.tipsParameterError();
  }
  return LzdAjax.post<unknown>(
    BASE_URL + '/student/update',
    {
      where: RParam.buildWhere({ userid: data.userid }),
      update: RParam.buildEdit(
        data,
        (k: string) => k !== 'seq' && k !== 'userid'
      )
    },
    false,
    true
  ).then((v) => v.data ?? []);
}

/** 更新学生通过userid major */
async function studentUpdate(data: TenantStudent) {
  if (!data.userid && !data.major) {
    return LzdUtil.tipsParameterError();
  }
  return LzdAjax.post<unknown>(
    BASE_URL + '/student/update',
    {
      where: RParam.buildWhere({ userid: data.userid, major: data.major }),
      update: RParam.buildEdit(
        data,
        (k: string) => k !== 'seq' && k !== 'userid'
      )
    },
    false,
    true
  ).then((v) => v.data ?? []);
}

/**
 * 修改学生
 */
export async function updateByManager(params: RP) {
  return LzdAjax.post<unknown>(BASE_URL + '/manager/update', params);
}

/**
 * 修改学生
 */
export async function updateByTeacher(params: RP) {
  return LzdAjax.post<unknown>(BASE_URL + '/teacher/update', params);
}

/**
 * 查询学生信息 列表
 */
export async function queryStudentKvsBy(
  username: string,
  college: string[],
  major: string[]
) {
  return LzdAjax.post<KV[]>(
    BASE_URL + '/query/list/kvs',
    {
      where: RParam.buildWhereLike({ username, college, major }, 'username')
    },
    false,
    true
  ).then((v) => v.data ?? []);
}

/**修改学生检测次数 */
export async function updateTeachersFileds(param: RP) {
  const res = await request.post<R<any>>(
    '/bsl/tenant/teachers/manager/update/fields',
    param
  );
  return res.data.code === 0 ? res.data.message : Promise.reject(res.data);
}

/**
 * 修改学生题目
 */
export async function updateDocTitle(
  projectId: string,
  userid: string,
  major: string,
  updateTitle: string
) {
  if (!projectId) {
    return LzdUtil.tipsError('projectId is error');
  }
  if (!updateTitle) {
    return LzdUtil.tipsError('updateTitle is error');
  }
  if (!userid) {
    return LzdUtil.tipsError('userid is error');
  }
  if (!major) {
    return LzdUtil.tipsError('major is error');
  }
  const others: Record<string, unknown> = { userid, major, updateTitle };
  return LzdAjax.post<unknown>(
    BASE_URL + '/title/change',
    {
      dataScopeId: getDataScopeId(),
      appId: getAppId(),
      roleCode: getRoleCode(),
      projectId,
      others
    },
    false,
    true
  );
}

/**
 * 查询学生数
 */
export async function managerCount(params: RP) {
  const res = await request.post<R<number>>(`${BASE_URL}/count`, params);
  if (res.data.code === 0) {
    return res.data.data;
  }
  return Promise.reject(new Error(res.data.message));
}

/**
 * 教师查询学生评优列表
 */
async function teacherQueryExcellentList(param: RP) {
  return LzdAjax.post<
    {
      studentSeq: number;
      userid: string;
      username: string;
      major: string;
      college: string;
      department?: string;
      teaching?: string;
      graduation?: string;
      adminclass?: string;
      advisorId?: string;
      advisorName?: string;
      title?: string;
      score?: string;
      docInsts?: DocumentInst[];
    }[]
  >(BASE_URL + '/teacher/query/excellent/list', param, false, true).then(
    (v) => v.data ?? []
  );
}

/** 学生 API */
class tenantStudentApi {
  static queryPage = pageTenantStudent;
  static teacherQueryExpPage = teacherQueryExpPage;
  static teacherQueryExpStats = teacherQueryExpStats;
  static queryTenantStudentsByTeacherIds = queryTenantStudentsByTeacherIds;
  static queryTenantStudentsByTeacher = queryTenantStudentsByTeacher;
  static querySingleByUserIdAndMajor = querySingleByUserIdAndMajor;
  static querySingleByTask = querySingleByTask;
  static studentUpdateByUserid = studentUpdateByUserid;
  static studentUpdate = studentUpdate;
  static queryStudentKvsBy = queryStudentKvsBy;
  static updateByManager = updateByManager;
  static updateByTeacher = updateByTeacher;
  static managerQueryList = managerQueryList;
  static teacherQueryList = teacherQueryList;
  static updateDocTitle = updateDocTitle;
  static managerCount = managerCount;
  static teacherQueryExcellentList = teacherQueryExcellentList;
}

export default tenantStudentApi;
